import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, SimpleChanges } from '@angular/core';
import { getColorForValue } from './utils';
import { distinctUntilChanged, map, Observable, Subject, takeUntil } from 'rxjs';

let uniqueGradientId = 0;

@Component({
  selector: 'line-graph',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      :host {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    `,
  ],
  template: `
    <svg [attr.width]="width" [attr.height]="height" [attr.viewBox]="'0 0 ' + width + ' ' + height" fill="none">
      <!-- Actual path rendered from data -->
      <path
        *ngIf="pathD"
        [attr.d]="pathD"
        [attr.stroke]="'url(#' + gradientId + ')'"
        stroke-linecap="round"
        fill="none"
        stroke-width="2"
      />

      <!-- Placeholder when no data -->
      <text *ngIf="!pathD" x="5" [attr.y]="height / 2" font-size="10" fill="gray" dominant-baseline="central">
        No data
      </text>

      <!-- Gradient Definition -->
      <defs>
  <linearGradient
    [attr.id]="gradientId"
    x1="0"
    y1="0"
    [attr.x2]="width"
    [attr.y2]="0"
    gradientUnits="userSpaceOnUse"
  >
    <ng-container *ngFor="let stop of gradientStops">
      <stop [attr.offset]="stop.offset" [attr.stop-color]="stop.color" />
    </ng-container>
  </linearGradient>
</defs>

    </svg>
  `,
})
export class LineGraphComponent {
  @Input() data: number[] = [];
  width: number = 50;
  height: number = 18;

  gradientId: string = 'line_graph_gradient_' + uniqueGradientId++;
  pathD: string = '';

  private readonly resizeObserver: Observable<{ width: number; height: number }>;
  private readonly destroy$ = new Subject<void>();

  constructor(
    readonly el: ElementRef<HTMLElement>,
    private readonly cd: ChangeDetectorRef
  ) {
    // Create an observable to watch for size changes
    this.resizeObserver = new Observable<void>(observer => {
      const resizeObserver = new ResizeObserver(() => {
        observer.next();
      });
      resizeObserver.observe(this.el.nativeElement);

      return () => resizeObserver.disconnect();
    })
      .pipe(
        map(() => {
          const width = this.el.nativeElement.clientWidth;
          const height = this.el.nativeElement.clientHeight;
          return { width, height };
        }),
        distinctUntilChanged((prev, curr) => prev.width === curr.width && prev.height === curr.height)
      );

    // Subscribe to the resize observer to update dimensions
    this.resizeObserver
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ width, height }) => {
        this.width = width;
        // this.height = height;
        this.generatePath();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.generatePath();
    }
  }

  gradientStops: { offset: number; color: string }[] = [];

  generatePath(): void {
    if (!this.data || this.data.length < 2) {
      this.pathD = '';
      this.gradientStops = [];
      return;
    }

    const stepX = this.width / (this.data.length - 1);
    const maxY = Math.max(...this.data);
    const minY = Math.min(...this.data);
    const rangeY = maxY - minY || 1;

    const scaled = this.data.map(v => this.height - ((v - minY) / rangeY) * this.height);

    // Build path
    let d = `M0,${scaled[0]}`;
    for (let i = 1; i < scaled.length; i++) {
      const prevX = (i - 1) * stepX;
      const currX = i * stepX;
      const midX = (prevX + currX) / 2;
      const midY = (scaled[i - 1] + scaled[i]) / 2;

      d += ` Q${prevX},${scaled[i - 1]} ${midX},${midY}`;
    }

    const lastX = (this.data.length - 1) * stepX;
    d += ` T${lastX},${scaled[scaled.length - 1]}`;
    this.pathD = d;

    // Generate gradient stops at "turns"
    this.gradientStops = [];

    for (let i = 1; i < this.data.length - 1; i++) {
      const prev = this.data[i - 1];
      const curr = this.data[i];
      const next = this.data[i + 1];

      if ((curr > prev && curr > next) || (curr < prev && curr < next)) {
        const offset = (i / (this.data.length - 1)); // relative x-position
        const color = getColorForValue(1, curr, maxY);
        this.gradientStops.push({ offset, color });
      }
    }

    // Always add a start and end stop
    this.gradientStops.unshift({ offset: 0, color: getColorForValue(1, this.data[0], maxY) });
    this.gradientStops.push({ offset: 1, color: getColorForValue(1, this.data[this.data.length - 1], maxY) });
    this.cd.markForCheck();
  }
}
